import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar, Box, Toolbar, IconButton, Container, useMediaQuery, alpha
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../../context/AuthContext';
import NavLogo from './navbar/NavLogo';
import NavItems from './navbar/NavItems';
import UserMenu from './navbar/UserMenu';
import AuthButtons from './navbar/AuthButtons';
import MobileDrawer from './navbar/MobileDrawer';
import { ROUTES } from '../../constants/routes';

/**
 * Navbar - Main navigation bar component
 * Orchestrates all navigation sub-components and handles mobile/desktop views
 */
const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { currentUser } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const isHomePage = location.pathname === ROUTES.HOME;

  // Handle scroll effect for navbar
  const scrolledRef = useRef(scrolled);
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolledRef.current) {
        scrolledRef.current = isScrolled;
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={scrolled ? 4 : 0}
        sx={{
          transition: 'all 0.3s ease',
          boxShadow: scrolled ?
            '0 4px 18px rgba(0,0,0,0.12)' :
            isHomePage ? 'none' : '0 2px 10px rgba(0,0,0,0.08)',
          borderBottom: !scrolled && !isHomePage ? `1px solid ${alpha(theme.palette.primary.main, 0.1)}` : 'none',
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar
            sx={{
              px: { xs: 2, sm: 3, md: 4 },
              py: scrolled ? 0.5 : 0.75,
              transition: 'all 0.3s ease',
              justifyContent: 'space-between',
              position: 'relative',
            }}
          >
            {/* Left section: Logo & Page Icon/Title */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              flex: isMobile ? '1 1 auto' : '0 1 auto'
            }}>
              <NavLogo />
            </Box>

            {/* Middle section: Navigation - Only show on desktop */}
            {!isMobile && <NavItems currentUser={currentUser} />}

            {/* Right section: Auth buttons or user menu */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexShrink: 0,
            }}>
              {/* Authentication Buttons or User Menu */}
              {!currentUser ? <AuthButtons /> : <UserMenu />}

              {/* Mobile Menu Button */}
              {isMobile && (
                <IconButton
                  size="large"
                  edge="end"
                  color="inherit"
                  aria-label="menu"
                  onClick={toggleDrawer}
                  sx={{
                    ml: 1,
                    background: alpha(theme.palette.common.white, 0.1),
                    '&:hover': {
                      background: alpha(theme.palette.common.white, 0.2),
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <MobileDrawer open={drawerOpen} onClose={toggleDrawer} />
    </>
  );
};

export default Navbar;
